//#region src/planner/onboarding-plan.ts
const RERUN_SETUP_HINT = "rerun_setup";
function buildOnboardingPlan(facts) {
	const decisions = [];
	if (!facts.auth.signedIn) {
		decisions.push(decide("sign_in", "available", true, "auth.signed_out"));
		return finalize(decisions);
	}
	const setupCommand = resolveSetupCommand(facts.context);
	decisions.push(decide("sign_in", "done", false, "auth.signed_in"));
	if (facts.auth.actingDisplayName === null) {
		decisions.push(decide("set_display_name", "available", true, "identity.name_missing"));
		return finalize(decisions);
	}
	decisions.push(decide("set_display_name", "done", false, "identity.name_present"));
	if (facts.space === null) {
		decisions.push(decide("enter_space", "available", true, "space.none_in_context"));
		return finalize(decisions);
	}
	if (!facts.space.actingProfileIsMember) {
		decisions.push(decide("enter_space", "available", true, "space.not_joined"));
		return finalize(decisions);
	}
	decisions.push(decide("enter_space", "done", false, "space.joined"));
	decisions.push(resolveBringAgentsDecision({
		facts,
		space: facts.space,
		actingProfileId: facts.auth.actingProfileId
	}));
	decisions.push(resolveConnectComputerDecision({
		deviceStatus: facts.device.status,
		setupCommand
	}));
	return finalize(decisions);
}
function resolveSetupCommand(context) {
	if (context.kind === "web_setup_handoff") return `wakeloop setup --start-session ${context.startSessionToken} --view agent`;
	return "wakeloop setup --view agent";
}
function decide(id, status, blocking, reason, run = null, afterRun = null) {
	return {
		id,
		status,
		blocking,
		reason,
		run,
		afterRun
	};
}
function activeAgentProfiles(profiles) {
	return profiles.filter((profile) => profile.kind === "agent" && profile.active);
}
function resolveBringAgentsDecision(input) {
	const agents = activeAgentProfiles(input.facts.accountProfiles);
	if (agents.length === 0) return decide("bring_agents", "blocked", false, "agents.none_yet");
	const members = new Set(input.space.memberAgentProfileIds);
	if (agents.some((agent) => members.has(agent.profileId))) return decide("bring_agents", "done", false, "agents.member_present");
	return decide("bring_agents", "available", false, "agents.addable_present", `wakeloop space add-agents ${input.space.spaceId} --profile ${input.actingProfileId} --all --view agent`);
}
function resolveConnectComputerDecision(input) {
	const { setupCommand } = input;
	switch (input.deviceStatus) {
		case "connected": return decide("connect_this_computer", "done", false, "computer.connected");
		case "connecting": return decide("connect_this_computer", "available", false, "computer.connecting", setupCommand, RERUN_SETUP_HINT);
		case "not_connected": return decide("connect_this_computer", "available", false, "computer.not_connected", setupCommand, RERUN_SETUP_HINT);
		case "unknown": return decide("connect_this_computer", "blocked", false, "computer.evidence_unavailable");
		default: return assertNeverDeviceStatus(input.deviceStatus);
	}
}
function finalize(decisions) {
	return {
		decisions,
		summary: decisions.some((decision) => decision.status === "available" || decision.status === "blocked") ? "actions_pending" : "nothing_to_do"
	};
}
function assertNeverDeviceStatus(value) {
	throw new Error(`unexpected device status: ${String(value)}`);
}
//#endregion
//#region src/presentation/profile-readiness.ts
function buildDaemonRouteObservationPresentation(observation) {
	if (observation.status === "online") return {
		detail: "WakeLoop server can see an active reply route for this profile.",
		statusLabel: "Online"
	};
	if (observation.status === "offline") return {
		detail: "WakeLoop server can't currently see an active reply route for this profile.",
		statusLabel: "Offline"
	};
	switch (observation.reasonCode) {
		case "server_unsupported": return {
			detail: "This WakeLoop server can't report route status yet.",
			statusLabel: "Unavailable"
		};
		case "profile_not_agent": return {
			detail: "Only agent profiles have a reply route.",
			statusLabel: "Unavailable"
		};
		case "profile_not_selected": return {
			detail: "Choose an Agent Profile to check reply route status.",
			statusLabel: "Unavailable"
		};
		case "not_requested": return {
			detail: "WakeLoop did not ask for reply route status.",
			statusLabel: "Unavailable"
		};
		default: return {
			detail: "WakeLoop couldn't confirm reply route status yet.",
			statusLabel: "Unavailable"
		};
	}
}
//#endregion
//#region src/presentation/setup-flow-copy.ts
const SETUP_FLOW_COPY = {
	commandPlaceholder: "Building setup command...",
	connectTerminalCompleteSummary: "Terminal connected.",
	connectTerminalDisconnectedSummary: "Terminal disconnected. Run a fresh setup command.",
	connectTerminalPendingSummary: "Run the setup command in Terminal.",
	copyCommandLabel: "Copy setup command",
	expiredCommandLabel: "Setup command expired.",
	expiredCommandPlaceholder: "Refresh the setup command to continue.",
	mode: "setup",
	pageDescription: "Run one command. This page updates when Terminal connects.",
	pageTitle: "Connect this computer",
	prepareComputerActiveSummaryByInternalStep: {
		prepare_selected_agents: "Enabling local agents.",
		refresh_service: "Preparing WakeLoop Service.",
		report_runtime: "Connecting this computer.",
		verify_setup: "Checking setup."
	},
	prepareComputerBlockedSummary: "Setup needs attention.",
	prepareComputerCompleteSummary: "Computer set up.",
	prepareComputerDefaultActiveSummary: "Setting up this computer.",
	prepareComputerNoLocalAgentsSummary: "No local agents selected.",
	prepareComputerPendingSummary: "Waiting for Terminal.",
	prepareComputerTerminalDisconnectedSummary: "Terminal disconnected. Run a fresh setup command.",
	prepareComputerTitle: "Connect this computer",
	readyAccountComputerActionSummary: "Setup complete. Back to Computers, or run setup again.",
	readyCompleteSummary: "Setup complete.",
	readyDefaultActionSummary: "Continue, or run setup again.",
	readyIncompleteSummary: "Setup not finished yet.",
	readyNoSelectedLocalAgentsActionSummary: "No local agents selected. Continue, or run setup again.",
	readyNoSelectedLocalAgentsSummary: "Setup complete. No local agents selected.",
	readyRefreshPendingActionSummary: "Continue after the check, or run setup again.",
	readyRefreshErrorSummary: "Setup complete. Wake availability could not refresh.",
	readyRefreshPendingSummary: "Checking Wake availability.",
	readySpaceRecoveryActionSummary: "Setup complete. Back to Space, or run setup again.",
	readyTitle: "Setup complete 🎉",
	cliAiAgentHintTemplate: "AI setup agents: run {command} for setup.complete.",
	cliBootstrapRepairStatus: "Preparing WakeLoop...",
	cliBootstrapRunStatus: "Starting WakeLoop...",
	cliCompletionTitle: "✓ WakeLoop Setup Complete 🎉",
	cliProgressCheckingLocalAgents: "Checking local agents...",
	cliProgressConnectingLocalAgents: "Enabling local agents...",
	cliProgressFinishingSetup: "Finishing setup...",
	cliProgressStartingService: "Preparing WakeLoop Service...",
	cliProgressUpdatingSkills: "Updating WakeLoop skills..."
};
const UPDATE_SERVICE_FLOW_COPY = {
	commandPlaceholder: "Building update command...",
	connectTerminalCompleteSummary: "Terminal connected.",
	connectTerminalDisconnectedSummary: "Terminal disconnected. Run a fresh update command.",
	connectTerminalPendingSummary: "Run the update command in Terminal.",
	copyCommandLabel: "Copy update command",
	expiredCommandLabel: "Update command expired.",
	expiredCommandPlaceholder: "Refresh the update command to continue.",
	mode: "update_service",
	pageDescription: "Run one command. This page updates when Terminal reconnects.",
	pageTitle: "Update WakeLoop Service",
	prepareComputerActiveSummaryByInternalStep: {
		prepare_selected_agents: "Checking local agents.",
		refresh_service: "Updating WakeLoop Service.",
		report_runtime: "Connecting this computer.",
		verify_setup: "Checking update."
	},
	prepareComputerBlockedSummary: "Update needs attention.",
	prepareComputerCompleteSummary: "WakeLoop Service updated.",
	prepareComputerDefaultActiveSummary: "Updating WakeLoop Service.",
	prepareComputerNoLocalAgentsSummary: "WakeLoop Service updated.",
	prepareComputerPendingSummary: "Waiting for Terminal.",
	prepareComputerTerminalDisconnectedSummary: "Terminal disconnected. Run a fresh update command.",
	prepareComputerTitle: "Update WakeLoop Service",
	readyAccountComputerActionSummary: "Update complete. Back to Computers, or run it again.",
	readyCompleteSummary: "Update complete.",
	readyDefaultActionSummary: "Continue, or run it again.",
	readyIncompleteSummary: "Update not finished yet.",
	readyNoSelectedLocalAgentsActionSummary: "Update complete. Continue, or run it again.",
	readyNoSelectedLocalAgentsSummary: "Update complete.",
	readyRefreshPendingActionSummary: "Continue after the check, or run it again.",
	readyRefreshErrorSummary: "Update complete. Wake availability could not refresh.",
	readyRefreshPendingSummary: "Checking Wake availability.",
	readySpaceRecoveryActionSummary: "Update complete. Back to Space, or run it again.",
	readyTitle: "Update complete",
	cliAiAgentHintTemplate: "AI setup agents: run {command} for setup.complete.",
	cliBootstrapRepairStatus: "Preparing WakeLoop...",
	cliBootstrapRunStatus: "Starting WakeLoop...",
	cliCompletionTitle: "✓ WakeLoop Service Update Complete",
	cliProgressCheckingLocalAgents: "Checking local agents...",
	cliProgressConnectingLocalAgents: "Checking local agents...",
	cliProgressFinishingSetup: "Finishing update...",
	cliProgressStartingService: "Updating WakeLoop Service...",
	cliProgressUpdatingSkills: "Updating WakeLoop skills..."
};
function getSetupFlowCopy(mode) {
	return mode === "update_service" ? UPDATE_SERVICE_FLOW_COPY : SETUP_FLOW_COPY;
}
function resolveSetupComputerActiveSummary(input) {
	if (isSetupComputerInternalStepId(input.stepId)) return input.copy.prepareComputerActiveSummaryByInternalStep[input.stepId];
	return input.copy.prepareComputerDefaultActiveSummary;
}
function isSetupComputerInternalStepId(value) {
	return value === "prepare_selected_agents" || value === "refresh_service" || value === "report_runtime" || value === "verify_setup";
}
//#endregion
//#region src/recovery/computer-reply-readiness.ts
const LEADING_VERSION_PREFIX_RE = /^v/u;
const SEMVER_CORE_RE = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/u;
function resolveComputerReplyReadiness(input) {
	if (input.currentDeviceTarget?.availability !== "resolved") return buildReadiness("needs_setup", "current_device.missing");
	const completedPrepareReadiness = resolveCompletedPrepareSessionReadiness({
		currentDeviceTarget: input.currentDeviceTarget,
		currentExpectedServiceVersion: input.currentExpectedServiceVersion ?? null,
		snapshot: input.completedPrepareSessionSnapshot ?? null
	});
	if (completedPrepareReadiness !== null) return completedPrepareReadiness;
	if (input.selectedExecutionTarget === void 0) return buildReadiness("checking", "start_state.checking");
	const executionTargetReadiness = resolveExecutionTargetReadiness(input.selectedExecutionTarget);
	if (executionTargetReadiness !== null) return buildReadiness(executionTargetReadiness.status, executionTargetReadiness.reasonCode, null, input.selectedExecutionTarget);
	if (input.selectedExecutionTarget === null) return buildReadiness("unknown", "execution_target.unknown", null, input.selectedExecutionTarget);
	const localAgentReadiness = resolveLocalAgentReadiness(input.selectedExecutionTarget.localAgentCatalog.entries);
	if (localAgentReadiness !== null) return buildReadiness(localAgentReadiness.status, localAgentReadiness.reasonCode, null, input.selectedExecutionTarget);
	return buildReadiness("ready", "ready", null, input.selectedExecutionTarget);
}
function shouldUseCompletedPrepareSessionForComputerReadiness(input) {
	const { snapshot } = input;
	if (input.currentDeviceTarget?.availability !== "resolved" || snapshot === null || snapshot === void 0 || snapshot.lifecycleStatus !== "completed" || snapshot.deviceId !== input.currentDeviceTarget.deviceId || snapshot.runtimeLane !== input.currentDeviceTarget.runtimeLane || snapshot.localAgentCatalog === null || !isPrepareStepComplete(snapshot, "refresh_service")) return false;
	return true;
}
function resolveCompletedPrepareSessionReadiness(input) {
	const snapshot = input.snapshot;
	if (snapshot === null || !shouldUseCompletedPrepareSessionForComputerReadiness({
		currentDeviceTarget: input.currentDeviceTarget,
		snapshot
	}) || snapshot.localAgentCatalog === null) return null;
	const service = snapshot.localReadinessSnapshot === null ? null : resolveCompletedPrepareSessionServiceVersion({
		currentExpectedServiceVersion: input.currentExpectedServiceVersion,
		snapshot
	});
	if (snapshot.localReadinessSnapshot !== null) {
		const backgroundHelperReadiness = resolveBackgroundHelperReadiness(snapshot.localReadinessSnapshot.service);
		if (backgroundHelperReadiness !== null) return buildReadiness(backgroundHelperReadiness.status, backgroundHelperReadiness.reasonCode, service);
	}
	const localAgentReadiness = resolveLocalAgentReadiness(snapshot.localAgentCatalog.entries);
	if (localAgentReadiness !== null) return buildReadiness(localAgentReadiness.status, localAgentReadiness.reasonCode, service);
	return buildReadiness("ready", "ready", service);
}
function resolveExecutionTargetReadiness(executionTarget) {
	if (executionTarget === null || executionTarget === void 0) return null;
	if (executionTarget.status === "ready") return null;
	if (executionTarget.status === "partial") return {
		status: "needs_repair",
		reasonCode: "execution_target.partial"
	};
	if (executionTarget.status === "not_ready") return {
		status: "needs_repair",
		reasonCode: "execution_target.not_ready"
	};
	if (executionTarget.status === "stale") return {
		status: "needs_repair",
		reasonCode: "execution_target.stale"
	};
	return {
		status: "unknown",
		reasonCode: "execution_target.unknown"
	};
}
function resolveBackgroundHelperReadiness(backgroundHelper) {
	if (backgroundHelper.installStatus === "not_installed") return {
		status: "needs_setup",
		reasonCode: "background_helper.not_installed"
	};
	if (backgroundHelper.installStatus === "unknown") return {
		status: "unknown",
		reasonCode: "background_helper.unknown"
	};
	if (backgroundHelper.executionCompatibility?.status === "update_required") return {
		status: "needs_repair",
		reasonCode: "background_helper.update_required"
	};
	if (backgroundHelper.runtimeStatus === "not_running") return {
		status: "needs_repair",
		reasonCode: "background_helper.not_running"
	};
	if (backgroundHelper.runtimeStatus === "stale") return {
		status: "needs_repair",
		reasonCode: "background_helper.stale"
	};
	if (backgroundHelper.runtimeStatus === "unknown") return {
		status: "unknown",
		reasonCode: "background_helper.unknown"
	};
	return null;
}
function resolveLocalAgentReadiness(entries) {
	if (entries.length === 0) return {
		status: "needs_setup",
		reasonCode: "local_agent_preparation.not_found"
	};
	if (entries.filter((entry) => entry.enabled && entry.launchStatus === "ready").length === 0) return {
		status: "needs_setup",
		reasonCode: "local_agent_preparation.not_enabled"
	};
	return null;
}
function resolveCompletedPrepareSessionServiceVersion(input) {
	const service = input.snapshot.localReadinessSnapshot?.service ?? null;
	const expectedVersion = input.currentExpectedServiceVersion ?? service?.expectedVersion ?? null;
	return {
		installStatus: service?.installStatus === "installed_current" && isOlderVersion(service.installedVersion, expectedVersion) ? "installed_outdated" : service?.installStatus ?? null,
		runtimeStatus: service?.runtimeStatus ?? null,
		installedVersion: service?.installedVersion ?? null,
		expectedVersion,
		executionCompatibility: service?.executionCompatibility ?? null,
		observedAt: input.snapshot.localReadinessSnapshot?.observedAt ?? null
	};
}
function isOlderVersion(installedVersion, expectedVersion) {
	if (!(installedVersion && expectedVersion)) return false;
	return compareSemver(installedVersion, expectedVersion) < 0;
}
function compareSemver(left, right) {
	const leftParts = parseSemverCore(left);
	const rightParts = parseSemverCore(right);
	if (!(leftParts && rightParts)) return left.localeCompare(right);
	for (let index = 0; index < leftParts.length; index += 1) {
		const leftPart = leftParts[index] ?? 0;
		const rightPart = rightParts[index] ?? 0;
		if (leftPart !== rightPart) return leftPart > rightPart ? 1 : -1;
	}
	return 0;
}
function parseSemverCore(value) {
	const match = value.trim().replace(LEADING_VERSION_PREFIX_RE, "").match(SEMVER_CORE_RE);
	if (!match) return null;
	return [
		Number(match[1]),
		Number(match[2]),
		Number(match[3])
	];
}
function isPrepareStepComplete(snapshot, stepId) {
	return snapshot.steps.some((step) => step.id === stepId && step.state === "complete");
}
function buildReadiness(status, reasonCode, service = null, executionTarget = null, detailReasonCode = null) {
	return {
		status,
		reasonCode,
		detailReasonCode,
		executionTargetStatus: executionTarget?.status ?? null,
		service
	};
}
//#endregion
export { buildDaemonRouteObservationPresentation, buildOnboardingPlan, getSetupFlowCopy, resolveComputerReplyReadiness, resolveSetupComputerActiveSummary, shouldUseCompletedPrepareSessionForComputerReadiness };

//# sourceMappingURL=index.js.map